export const SUPPORTED_LANGUAGES = ['en', 'zh'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';
export const FALLBACK_LANGUAGE: SupportedLanguage = DEFAULT_LANGUAGE;
export const LANGUAGE_STORAGE_KEY = 'seqreader.ui.language';

const supportedLanguageSet = new Set<string>(SUPPORTED_LANGUAGES);

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export const isSupportedLanguage = (value: unknown): value is SupportedLanguage =>
  typeof value === 'string' && supportedLanguageSet.has(value);

export const normalizeLanguage = (value: unknown): SupportedLanguage =>
  isSupportedLanguage(value) ? value : DEFAULT_LANGUAGE;

const getStorage = (): StorageLike | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const persistLanguage = (
  language: unknown,
  storage: StorageLike | null = getStorage()
) => {
  if (!storage) return;
  try {
    storage.setItem(LANGUAGE_STORAGE_KEY, normalizeLanguage(language));
  } catch {
    // Ignore storage write failures (private mode, quota, etc.)
  }
};

export const loadPersistedLanguage = (
  storage: StorageLike | null = getStorage()
): SupportedLanguage => {
  if (!storage) return DEFAULT_LANGUAGE;

  try {
    const raw = storage.getItem(LANGUAGE_STORAGE_KEY);
    const language = normalizeLanguage(raw);

    // Self-heal invalid local values to keep future startup deterministic.
    if (raw !== language) {
      storage.setItem(LANGUAGE_STORAGE_KEY, language);
    }

    return language;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};
